import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Empty, Spin } from '~shared/ui';
import { ILang } from '~shared/lib/i18n';
import { timezoneDayjs } from '~shared/lib/utils';

import type { ApiParentNotificationItem } from '../api';

interface ParentNotificationHistoryListViewProps {
  data: ApiParentNotificationItem[];
  loading?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

const eventDotColor: Record<string, string> = {
  parent_registration_success: '#0ACD9A',
  application_waiting_enroll: '#FF9500',
  child_enrolled: '#0ACD9A',
  child_expelled: '#ff4d4f',
  child_not_enrolled_docs_missing: '#ff4d4f',
  voucher_application_created: '#007AFF',
};

export const ParentNotificationHistoryListView: FC<ParentNotificationHistoryListViewProps> = ({
  data,
  loading,
  hasMore,
  onLoadMore,
}) => {
  const { i18n } = useTranslation();
  const lang = i18n.language as ILang;

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore) {
      return;
    }

    const node = sentinelRef.current;

    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (loading && !data?.length) {
    return (
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  if (!data?.length) {
    return <Empty />;
  }

  return (
    <div className="flex flex-col gap-3">
      {data.map((item) => {
        const orgName =
          lang === 'ky'
            ? item.organization_name_ky || item.organization_name_ru
            : item.organization_name_ru || item.organization_name_ky;

        const dotColor = eventDotColor[item.event] || '#cfcfcf';

        return (
          <div
            key={item.id}
            className={`relative rounded-lg border p-4 flex gap-3 ${
              item.unread
                ? 'bg-[rgba(10,205,154,0.06)] border-primary/30'
                : 'bg-white border-stroke'
            }`}
          >
            <span
              className="mt-2 shrink-0 rounded-full"
              style={{ width: 10, height: 10, background: dotColor }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="text-[15px] font-semibold text-black break-words">{item.title}</div>
                <div className="text-[12px] text-placeholder whitespace-nowrap">
                  {timezoneDayjs(item.created_at).format('DD.MM.YYYY HH:mm')}
                </div>
              </div>
              {item.body && (
                <div className="mt-1 text-[14px] text-black/80 whitespace-pre-wrap break-words">
                  {item.body}
                </div>
              )}
              {orgName && <div className="mt-2 text-[12px] text-placeholder">{orgName}</div>}
            </div>
            {item.unread && (
              <span
                className="absolute top-3 right-3 rounded-full bg-primary"
                style={{ width: 8, height: 8 }}
              />
            )}
          </div>
        );
      })}
      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-4">
          {loading && <Spin />}
        </div>
      )}
    </div>
  );
};
